import Layout from '@/components/Layout';
import { ProjectBoard } from '@/components/ProjectBoard';
import { ProjectList } from '@/components/ProjectList';
import { ProjectGantt } from '@/components/ProjectGantt';
import { ReleaseDetailsDialog } from '@/components/ReleaseDetailsDialog';
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from '@/context/AuthContext';
import { useAiPageContext } from '@/context/AiPageContextProvider';
import { useCreateTask } from '@/context/CreateTaskContext';
import { contextHelpers } from '@/components/AiCommandPalette';
import { useEffect, useState, Fragment, useRef } from 'react';
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash2, CheckCircle2, Archive, Zap } from "lucide-react";

import { SEO } from "@/components/SEO";

import { ProjectHeader } from "@/components/ProjectPage/ProjectHeader";
import ReleaseFeatureAnnouncement from '@/components/ReleaseFeatureAnnouncement';

const ProjectPage = () => {
    const { organizationSlug, projectKey, view } = useParams();
    const [storedSettings, setStoredSettings] = useState(null);
    const [previousView, setPreviousView] = useState(null);
    const stateRef = useRef({ view, previousView });
    const navigate = useNavigate();
    const location = useLocation();
    const { toast } = useToast();
    const [project, setProject] = useState(null);
    const [selectedRelease, setSelectedRelease] = useState(null);
    const [showReleaseDetailsDialog, setShowReleaseDetailsDialog] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showShipConfirmation, setShowShipConfirmation] = useState(false);
    const [showActivateConfirmation, setShowActivateConfirmation] = useState(false);
    const [showDeactivateConfirmation, setShowDeactivateConfirmation] = useState(false);
    const [showReopenConfirmation, setShowReopenConfirmation] = useState(false);
    const [releaseToDelete, setReleaseToDelete] = useState(null);
    const [releaseToShip, setReleaseToShip] = useState(null);
    const [releaseToActivate, setReleaseToActivate] = useState(null);
    const [releaseToDeactivate, setReleaseToDeactivate] = useState(null);
    const [releaseToReopen, setReleaseToReopen] = useState(null);
    const [isDeletingRelease, setIsDeletingRelease] = useState(false);
    const [isShippingRelease, setIsShippingRelease] = useState(false);
    const [isActivatingRelease, setIsActivatingRelease] = useState(false);
    const [isDeactivatingRelease, setIsDeactivatingRelease] = useState(false);
    const [isReopeningRelease, setIsReopeningRelease] = useState(false);
    const { user, axiosInstance } = useAuth();
    const { setCurrentAiPageContext } = useAiPageContext();
    const { openCreateTaskDialog, closeCreateTaskDialog } = useCreateTask();

    const [loadingStates, setLoadingStates] = useState({
        core: true,
        board: true,
        list: true,
    });

    // Get filter parameters from URL with "manual" as default sort
    const filterBy = storedSettings?.filterBy || 'none';
    const sortBy = storedSettings?.sortBy || 'manual';
    const groupByAssignee = storedSettings?.groupBy === 'assignee';

    // Get selected members from URL
    const selectedMembers = new Set(
        storedSettings?.filterByAssignee ? storedSettings?.filterByAssignee.split(',') : []
    );

    const updateProjectLocalStorage = (updates) => {
        const allProjects = JSON.parse(localStorage.getItem('projects')) || {};
        const currentProjectSettings = allProjects[projectKey] || {};
        const newProjectSettings = { ...currentProjectSettings, ...updates, projectKey };
        allProjects[projectKey] = newProjectSettings;
        localStorage.setItem('projects', JSON.stringify(allProjects));
        setStoredSettings(newProjectSettings);
    };

    const handleLocalStorageLoad = () => {
        const allProjects = JSON.parse(localStorage.getItem('projects')) || {};
        const storedProjectSettings = allProjects[projectKey];
        if (storedProjectSettings) {
            setStoredSettings(storedProjectSettings);
            if (storedProjectSettings.view !== view) {
                updateProjectLocalStorage({ view });
            }
        } else {
            const newProjectSettings = {
                projectKey,
                filterBy: 'none',
                sortBy: 'manual',
                groupBy: false,
                view: view
            };
            updateProjectLocalStorage(newProjectSettings);
        }
    };

    const toggleMemberFilter = (memberId) => {
        const currentMembers = new Set(selectedMembers);
        if (currentMembers.has(memberId)) {
            currentMembers.delete(memberId);
        } else {
            currentMembers.add(memberId);
        }
        if (currentMembers.size > 0) {
            updateProjectLocalStorage({filterByAssignee: Array.from(currentMembers).join(',')});
        } else {
            updateProjectLocalStorage({filterByAssignee: null});
        }
    };

    const handleFilterChange = (value) => {
        if (value === 'assigned') {
            updateProjectLocalStorage({
                filterBy: 'none',
                filterByAssignee: user.id
            });
        } else {
            updateProjectLocalStorage({
                filterBy: value,
                filterByAssignee: null
            });
        }
    };

    const handleSortChange = (value) => {
        updateProjectLocalStorage({ sortBy: value });
    };

    const handleGroupByChange = (checked) => {
        updateProjectLocalStorage({ groupBy: checked ? 'assignee' : null });
    };

    const handleOpenCreateTask = () => {
        openCreateTaskDialog({
            project: project,
            onSuccess: async () => {
                await fetchProject();
            }
        });
    };

    const fetchProject = async () => {
        try {
            const response = await axiosInstance.get(`/projects/${organizationSlug}/${projectKey}`);
            const projectData = response.data;
            setProject(projectData);
            setCurrentAiPageContext(
                contextHelpers.createProjectContext(projectData)
            );
        } catch (error) {
            console.error('Error fetching project:', error);
            toast({ description: 'Failed to fetch project details', variant: 'destructive' });
        } finally {
            setLoadingStates({ core: false, board: false, list: false });
        }
    };

    useEffect(() => {
        stateRef.current = { view, previousView };
    }, [view, previousView]);

    useEffect(() => {
        if (view && view !== previousView) {
            setPreviousView(view);
        }
    }, [view]);

    useEffect(() => {
        fetchProject();
        handleLocalStorageLoad();
        const handlePopState = () => {
            const currentState = stateRef.current;
            if (currentState.view !== currentState.previousView) {
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [projectKey, organizationSlug]);

    const handleViewChange = (newView) => {
        updateProjectLocalStorage({ view: newView });
        if (newView !== view) {
            navigate(`/${organizationSlug}/plan/${projectKey}/${newView}`);
        }
    };

    const handleShowReleaseDetails = (releaseOrId) => {
        let release = null;
        if (typeof releaseOrId === 'string') {
            release = project?.releases?.find(r => r._id === releaseOrId) || null;
        } else if (releaseOrId && typeof releaseOrId === 'object') {
            release = releaseOrId;
        }

        if (release) {
            setSelectedRelease(release);
            setShowReleaseDetailsDialog(true);
        }
    };

    const handleReleaseUpdate = (updatedRelease) => {
        setProject(prevProject => ({
            ...prevProject,
            releases: prevProject?.releases?.map(r => r._id === updatedRelease._id ? updatedRelease : r) || []
        }));
        setSelectedRelease(updatedRelease);
    };

    const handleReleaseActivate = (release) => {
        setReleaseToActivate(release);
        setShowActivateConfirmation(true);
    };

    const confirmActivateRelease = async () => {
        if (!releaseToActivate) return;
        setIsActivatingRelease(true);
        try {
            await axiosInstance.patch(`/projects/${project.organizationSlug}/${project.key}/releases/${releaseToActivate._id}`, { startDate: new Date() });
            await fetchProject();
            setShowActivateConfirmation(false);
            setReleaseToActivate(null);
            setShowReleaseDetailsDialog(false);
            setSelectedRelease(null);
            toast({ description: `Release "${releaseToActivate.name}" activated successfully` });
        } catch (error) {
            console.error('Error activating release:', error);
            toast({ variant: 'destructive', description: error.response?.data?.message || 'Failed to activate release' });
        } finally {
            setIsActivatingRelease(false);
        }
    };

    const handleReleaseDeactivate = (release) => {
        setReleaseToDeactivate(release);
        setShowDeactivateConfirmation(true);
    };

    const confirmDeactivateRelease = async () => {
        if (!releaseToDeactivate) return;
        setIsDeactivatingRelease(true);
        try {
            await axiosInstance.patch(`/projects/${project.organizationSlug}/${project.key}/releases/${releaseToDeactivate._id}`, { startDate: null });
            await fetchProject();
            setShowDeactivateConfirmation(false);
            setReleaseToDeactivate(null);
            setShowReleaseDetailsDialog(false);
            setSelectedRelease(null);
            toast({ description: `Release "${releaseToDeactivate.name}" moved to backlog` });
        } catch (error) {
            console.error('Error deactivating release:', error);
            toast({ variant: 'destructive', description: error.response?.data?.message || 'Failed to move release to backlog' });
        } finally {
            setIsDeactivatingRelease(false);
        }
    };

    const handleReleaseReopen = (release) => {
        setReleaseToReopen(release);
        setShowReopenConfirmation(true);
    };

    const confirmReopenRelease = async () => {
        if (!releaseToReopen) return;
        setIsReopeningRelease(true);
        try {
            await axiosInstance.patch(`/projects/${project.organizationSlug}/${project.key}/releases/${releaseToReopen._id}`, { actualReleaseDate: null });
            await fetchProject();
            setShowReopenConfirmation(false);
            setReleaseToReopen(null);
            setShowReleaseDetailsDialog(false);
            setSelectedRelease(null);
            toast({ description: `Release "${releaseToReopen.name}" reopened successfully` });
        } catch (error) {
            console.error('Error reopening release:', error);
            toast({ variant: 'destructive', description: error.response?.data?.message || 'Failed to reopen release' });
        } finally {
            setIsReopeningRelease(false);
        }
    };

    const handleReleaseDelete = (release) => {
        setReleaseToDelete(release);
        setShowDeleteConfirmation(true);
    };

    const confirmDeleteRelease = async () => {
        if (!releaseToDelete) return;
        setIsDeletingRelease(true);
        try {
            await axiosInstance.delete(`/projects/${project.organizationSlug}/${project.key}/releases/${releaseToDelete._id}`);
            await fetchProject();
            setShowReleaseDetailsDialog(false);
            setSelectedRelease(null);
            setShowDeleteConfirmation(false);
            setReleaseToDelete(null);
            toast({ description: `Release "${releaseToDelete.name}" deleted successfully` });
        } catch (error) {
            console.error('Error deleting release:', error);
            toast({ variant: 'destructive', description: error.response?.data?.message || 'Failed to delete release' });
        } finally {
            setIsDeletingRelease(false);
        }
    };

    const handleReleaseShip = (release) => {
        setReleaseToShip(release);
        setShowShipConfirmation(true);
    };

    const confirmShipRelease = async () => {
        if (!releaseToShip) return;
        setIsShippingRelease(true);
        try {
            await axiosInstance.patch(`/projects/${project.organizationSlug}/${project.key}/releases/${releaseToShip._id}`, { actualReleaseDate: new Date() });
            await fetchProject();
            setShowReleaseDetailsDialog(false);
            setSelectedRelease(null);
            setShowShipConfirmation(false);
            setReleaseToShip(null);
            toast({ description: `Release "${releaseToShip.name}" shipped successfully` });
        } catch (error) {
            console.error('Error shipping release:', error);
            toast({ variant: 'destructive', description: error.response?.data?.message || 'Failed to ship release' });
        } finally {
            setIsShippingRelease(false);
        }
    };

    return (
        <Layout>
            <SEO title={project?.name ? `${project.name} › Active week` : 'Active week'} />

            <ReleaseFeatureAnnouncement />

            <div className="space-y-4 flex flex-col h-full">
                <ProjectHeader
                    isLoading={loadingStates.core}
                    project={project}
                    selectedMembers={selectedMembers}
                    toggleMemberFilter={toggleMemberFilter}
                    user={user}
                    filterBy={filterBy}
                    sortBy={sortBy}
                    groupByAssignee={groupByAssignee}
                    view={view}
                    handleFilterChange={handleFilterChange}
                    handleSortChange={handleSortChange}
                    handleGroupByChange={handleGroupByChange}
                    handleViewChange={handleViewChange}
                    handleOpenCreateTask={handleOpenCreateTask}
                    onShowReleaseDetails={handleShowReleaseDetails}
                />

                <div className="flex-grow">
                    {view === 'board' ? (
                        <ProjectBoard
                            isLoading={loadingStates.board}
                            project={project}
                            setProject={setProject}
                            groupByAssignee={groupByAssignee}
                            filterByAssignee={selectedMembers}
                            filterBy={filterBy}
                            sortBy={sortBy}
                            user={user}
                            onShowReleaseDetails={handleShowReleaseDetails}
                        />
                    ) : view === 'release' ? (
                        <ProjectGantt
                            isLoading={loadingStates.list}
                            project={project}
                            filterByAssignee={selectedMembers}
                            filterBy={filterBy}
                            sortBy={sortBy}
                            user={user}
                            onShowReleaseDetails={handleShowReleaseDetails}
                        />
                    ) : (
                        <ProjectList
                            isLoading={loadingStates.list}
                            project={project}
                            setProject={setProject}
                            groupByAssignee={groupByAssignee}
                            filterByAssignee={selectedMembers}
                            filterBy={filterBy}
                            sortBy={sortBy}
                            user={user}
                            refetchProject={fetchProject}
                            onShowReleaseDetails={handleShowReleaseDetails}
                        />
                    )}
                </div>
            </div>
            {/* Release Details Dialog */}
            <ReleaseDetailsDialog
                open={showReleaseDetailsDialog}
                onOpenChange={(open) => {
                    setShowReleaseDetailsDialog(open);
                    if (!open) {
                        setSelectedRelease(null);
                    }
                }}
                selectedRelease={selectedRelease}
                project={project}
                activeWeekTasks={[]}
                backlogReleaseGroups={{}}
                releases={project?.releases || []}
                onUpdate={handleReleaseUpdate}
                onActivate={handleReleaseActivate}
                onDeactivate={handleReleaseDeactivate}
                onReopen={handleReleaseReopen}
                onDelete={handleReleaseDelete}
                onComplete={handleReleaseShip}
            />

            {/* Delete Release Confirmation Dialog */}
            <Dialog open={showDeleteConfirmation} onOpenChange={setShowDeleteConfirmation}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                            Delete Release
                        </DialogTitle>
                        <DialogDescription className="space-y-3 pt-3">
                            <p>
                                Are you sure you want to delete <strong>"{releaseToDelete?.name}"</strong>?
                            </p>
                            <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                                <p className="text-sm text-red-800 dark:text-red-200">
                                    This action cannot be undone. All tasks assigned to this release will be removed from the release.
                                </p>
                            </div>
                        </DialogDescription>
                    </DialogHeader>
                    
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowDeleteConfirmation(false);
                                setReleaseToDelete(null);
                            }}
                            disabled={isDeletingRelease}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={confirmDeleteRelease}
                            disabled={isDeletingRelease}
                        >
                            {isDeletingRelease ? "Deleting..." : "Delete Release"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Ship Release Confirmation Dialog */}
            <Dialog open={showShipConfirmation} onOpenChange={setShowShipConfirmation}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                            Ship Release
                        </DialogTitle>
                        <DialogDescription className="space-y-3 pt-3">
                            <p>
                                Are you sure you want to mark <strong>"{releaseToShip?.name}"</strong> as shipped?
                            </p>
                            <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                                <p className="text-sm text-green-800 dark:text-green-200">
                                    This will set the release date to today and mark the release as completed.
                                </p>
                            </div>
                        </DialogDescription>
                    </DialogHeader>
                    
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowShipConfirmation(false);
                                setReleaseToShip(null);
                            }}
                            disabled={isShippingRelease}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmShipRelease}
                            disabled={isShippingRelease}
                        >
                            {isShippingRelease ? "Shipping..." : "Ship Release"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Activate Release Confirmation Dialog */}
            <Dialog open={showActivateConfirmation} onOpenChange={setShowActivateConfirmation}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            Activate Release
                        </DialogTitle>
                        <DialogDescription className="space-y-3 pt-3">
                            <p>
                                Are you sure you want to activate <strong>"{releaseToActivate?.name}"</strong>?
                            </p>
                            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                                <p className="text-sm text-blue-800 dark:text-blue-200">
                                    This will set the start date to today and move all tasks from the backlog to the active week.
                                </p>
                            </div>
                        </DialogDescription>
                    </DialogHeader>
                    
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowActivateConfirmation(false);
                                setReleaseToActivate(null);
                            }}
                            disabled={isActivatingRelease}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmActivateRelease}
                            disabled={isActivatingRelease}
                        >
                            {isActivatingRelease ? "Activating..." : "Activate Release"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Deactivate Release Confirmation Dialog */}
            <Dialog open={showDeactivateConfirmation} onOpenChange={setShowDeactivateConfirmation}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Archive className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                            Move to Backlog
                        </DialogTitle>
                        <DialogDescription className="space-y-3 pt-3">
                            <p>
                                Are you sure you want to move <strong>"{releaseToDeactivate?.name}"</strong> to the backlog?
                            </p>
                            <div className="bg-neutral-50 dark:bg-neutral-950/20 border border-neutral-200 dark:border-neutral-800 rounded-lg p-3">
                                <p className="text-sm text-neutral-800 dark:text-neutral-200">
                                    This will remove the start date and move all tasks from the active week back to the backlog.
                                </p>
                            </div>
                        </DialogDescription>
                    </DialogHeader>
                    
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowDeactivateConfirmation(false);
                                setReleaseToDeactivate(null);
                            }}
                            disabled={isDeactivatingRelease}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="outline"
                            onClick={confirmDeactivateRelease}
                            disabled={isDeactivatingRelease}
                        >
                            {isDeactivatingRelease ? "Moving..." : "Move to Backlog"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reopen Release Confirmation Dialog */}
            <Dialog open={showReopenConfirmation} onOpenChange={setShowReopenConfirmation}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            Reopen Release
                        </DialogTitle>
                        <DialogDescription className="space-y-3 pt-3">
                            <p>
                                Are you sure you want to reopen <strong>"{releaseToReopen?.name}"</strong>?
                            </p>
                            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                                <p className="text-sm text-blue-800 dark:text-blue-200">
                                    This will remove the shipped date and allow you to continue working on this release.
                                </p>
                            </div>
                        </DialogDescription>
                    </DialogHeader>
                    
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowReopenConfirmation(false);
                                setReleaseToReopen(null);
                            }}
                            disabled={isReopeningRelease}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmReopenRelease}
                            disabled={isReopeningRelease}
                        >
                            {isReopeningRelease ? "Reopening..." : "Reopen Release"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
};

const LoadingState = () => (
    <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center space-x-4 animate-pulse">
        <AiOutlineLoading3Quarters className="text-2xl text-primary animate-spin" />
        <h1 className="text-xl font-semibold">Loading project details...</h1>
    </div>
);

export default ProjectPage;